// shared-lib/cabinet-runs/catalog.js
//
// WHAT a cabinet run may be made of — the vocabulary, as opposed to
// `profiles.js`, which says how big the pieces are.
//
// Three catalogs, one rule. A LOWER bay wears a front (doors, a drawer bank, a
// sink apron…), an UPPER bay wears a different one (its own vocabulary — no
// drawers, no sink, but `open-shelf` and `none`), and an appliance bay has a
// KIND (what stands in the slot: a dishwasher, a range…). Each entry names
// itself once and declares which dimension PROFILES offer it — absent
// `profiles` means every profile, so the shared vocabulary stays unannotated
// and only a genuinely profile-specific piece carries a list. Adding one (a
// kitchen spice pull-out, a bathroom hamper) is one entry here and no edit
// anywhere else.
//
// This follows the convention `designCenterCategories` / `designPackageSlots`
// already use for room kinds: the ENTRY declares where it belongs and the
// consumer filters, rather than each consumer branching on the room. The
// alternative — every consumer testing `roomKind !== 'bathroom'` — puts the
// same rule in as many places as there are surfaces, keyed on the ROOM rather
// than on the run, which is the thing `profile` exists to stop re-guessing.
//
// BEHAVIOUR lives elsewhere and is not duplicated here: `applyBayType`
// (`bayOverrides.js`) builds a front, `bays.js` owns what a kind means to the
// packer. This module owns the id, the words the UI shows, and the
// membership. `catalog.ci.test.js` fails if the two ever name different sets.

'use strict';

const {
  KITCHEN_PROFILE_ID,
  VANITY_PROFILE_ID,
  profileById,
} = require('./profiles');
const { HOSTED_BAY_KINDS } = require('./bays');
const { frontWidthUnavailableReason } = require('./bayWidths');
const { sinkBayFrontReason } = require('./sinkUnits');
const { floatingShelfReason } = require('./floatingShelves');
const { endShelfReason, END_SHELF_WIDTH_IN } = require('./endShelves');
const { PULL_OUTS, pullOutReason } = require('./pullOuts');
const {
  MICROWAVE_CABINETS,
  microwaveCabinetReason,
} = require('./microwaveCabinets');
const {
  wallDrawersReason,
  wallDrawersHangFix,
  WALL_DRAWERS,
} = require('./wallDrawers');
const { valanceBayReason } = require('./valanceBays');
const { RACKS, rackReason, rackWidthM } = require('./racks');
const { angledEndReason, ANGLED_ENDS } = require('./angledEnds');
const { floatingShelfUnits } = require('./floatingShelves');
const { familyOf } = require('./families');

/**
 * The FRONT a bay can wear, in picker order. `name` is the picker's word and
 * `short` the collapsed-header one, so a front cannot be called two different
 * things in two places.
 *
 * Every id here must be one `applyBayType` handles — that is a test, not a
 * convention. The six below are the vocabulary both kitchens and vanities use
 * (a vanity has drawer banks and a sink base too), so none carries `profiles`.
 * A kitchen-only front — the spice pull-out — would read:
 *
 *   { id: 'spice', name: 'Spice pull-out', short: 'Spice',
 *     profiles: [KITCHEN_PROFILE_ID] }
 *
 * and would need its arm in `applyBayType` in the same commit.
 */
const FRONT_TYPES = [
  { id: 'standard', name: 'Doors', short: 'Doors', group: 'cabinets' },
  { id: 'drawer', name: 'Drawer', short: 'Drawer', group: 'cabinets' },
  { id: 'drawer-2', name: '2 drawers', short: '2 drawers', group: 'cabinets' },
  {
    id: 'drawer-doors',
    name: 'Drawer + Doors',
    short: 'Drawer + Doors',
    group: 'cabinets',
  },
  { id: 'sink', name: 'Sink', short: 'Sink', group: 'cabinets' },
  { id: 'tall', name: 'Tall unit', short: 'Tall', group: 'cabinets' },
  // pull-out bases sold at one width (`pullOuts.js`)
  {
    id: 'trash',
    name: 'Trash pull-out',
    short: 'Trash',
    group: 'specials',
    profiles: [KITCHEN_PROFILE_ID],
  },
  {
    id: 'spice',
    name: 'Spice pull-out',
    short: 'Spice',
    group: 'specials',
    profiles: [KITCHEN_PROFILE_ID],
  },
  // an open microwave space over a drawer, the microwave in it
  // (`microwaveCabinets.js`)
  {
    id: 'microwave',
    name: 'Microwave',
    short: 'Microwave',
    group: 'specials',
    profiles: [KITCHEN_PROFILE_ID],
  },
  // open shelves round a run's open end, in a 12" end bay (`endShelves.js`)
  {
    id: 'end-shelf',
    name: 'End shelf',
    short: 'End shelf',
    group: 'ends',
    profiles: [KITCHEN_PROFILE_ID],
  },
  // a 24" end bay whose outer front corner is cut at 45° (`angledEnds.js`)
  {
    id: 'angled-end',
    name: 'Angled end',
    short: 'Angled',
    group: 'ends',
    profiles: [KITCHEN_PROFILE_ID],
  },
  { id: 'filler', name: 'Filler', short: 'Filler', group: 'other' },
];

/**
 * The front an UPPER bay can wear — a different vocabulary from the lower
 * tier's, not a subset of it. An upper has no drawers, no sink apron and
 * cannot be a tall unit, and it has one a lower does not: `none`, the stretch
 * of wall left deliberately bare. Built by `applyUpperOverrides`
 * (`upperBayOverrides.js`), whose `UPPER_FRONT_TYPES` set this is pinned
 * against.
 *
 * (Glass doors are NOT a front: they are a switch — a bay's `glass`, the
 * run's `upperSpec.glass` — that draws the `glass-front` DOOR STYLE after the
 * run's look, so a glass cabinet keeps its design and colour; `glassUppers.js`.)
 */
const UPPER_FRONT_TYPES = [
  { id: 'standard', name: 'Doors', short: 'Doors', group: 'cabinets' },
  // a door over two drawers, standing on the counter (`wallDrawers.js`)
  {
    id: 'wall-drawers',
    name: 'Wall with drawers',
    short: 'Drawers',
    group: 'cabinets',
  },
  // doors over an open microwave shelf, the microwave on it
  {
    id: 'microwave-shelf',
    name: 'Microwave shelf',
    short: 'Microwave',
    group: 'cabinets',
  },
  {
    id: 'open-shelf',
    name: 'Open shelf',
    short: 'Open shelf',
    group: 'shelves',
  },
  // boards on the wall, no box (`floatingShelves.js`, group 3b)
  {
    id: 'floating-shelves',
    name: 'Floating shelves',
    short: 'Shelves',
    group: 'shelves',
  },
  { id: 'end-shelf', name: 'End shelf', short: 'End shelf', group: 'shelves' },
  // a 12" x 12" end box with its door on the diagonal (`angledEnds.js`)
  { id: 'angled-end', name: 'Angled end', short: 'Angled', group: 'shelves' },
  // open racks hung from the top line (`racks.js`, group 5)
  { id: 'wine-rack', name: 'Wine rack', short: 'Wine rack', group: 'racks' },
  { id: 'wine-x', name: 'Wine X', short: 'Wine X', group: 'racks' },
  { id: 'plate-rack', name: 'Plate rack', short: 'Plates', group: 'racks' },
  { id: 'wine-cubes', name: 'Wine cubes', short: 'Cubes', group: 'racks' },
  // no cabinet, a valance across the top, kept over the sink
  // (`valanceBays.js`)
  { id: 'valance', name: 'Valance', short: 'Valance', group: 'other' },
  { id: 'filler', name: 'Filler', short: 'Filler', group: 'other' },
  { id: 'none', name: 'No cabinet', short: 'None', group: 'other' },
];

/**
 * The headings the picker groups the fronts under, in order (the user,
 * 2026-11-01: fourteen upper fronts were too many to scan as one row). A
 * front's `group` names one.
 */
const FRONT_GROUPS = Object.freeze([
  Object.freeze({ id: 'cabinets', name: 'Cabinets' }),
  Object.freeze({ id: 'shelves', name: 'Shelves' }),
  Object.freeze({ id: 'racks', name: 'Racks' }),
  Object.freeze({ id: 'specials', name: 'Specials' }),
  Object.freeze({ id: 'ends', name: 'Run ends' }),
  Object.freeze({ id: 'other', name: 'Other' }),
]);

/**
 * Fronts the user never PICKS, so they are kept out of both front lists and
 * out of every profile's offering — but they still need exactly one label.
 * `buildOvenHousing` builds an oven column around a scanned wall oven and the
 * run sizes it as one piece.
 */
const READ_ONLY_FRONTS = [
  { id: 'oven-column', name: 'Oven column', short: 'Oven' },
];

/**
 * The APPLIANCE bays a run can be given, by the bay kind `bays.js` packs.
 * `element` is the Add Elements tray's id for the same thing, so the tray can
 * be filtered without a second table mapping one to the other.
 *
 * A vanity run offers only the sink: `footprintFor` has no answer for a
 * dishwasher on a 21" body, and a bathroom has no use for the others. The
 * `tallUnit` linen tower is reached through the `tall` FRONT, not here, which
 * is why it is profile-neutral and absent from this list.
 */
const APPLIANCE_BAYS = [
  { kind: 'sink-base', element: 'crSink', name: 'Sink' },
  {
    kind: 'dishwasher',
    element: 'crDishwasher',
    name: 'Dishwasher',
    profiles: [KITCHEN_PROFILE_ID],
  },
  {
    kind: 'range',
    element: 'crRange',
    name: 'Range',
    profiles: [KITCHEN_PROFILE_ID],
  },
  {
    kind: 'fridge',
    element: 'crFridge',
    name: 'Fridge',
    profiles: [KITCHEN_PROFILE_ID],
  },
];

/**
 * Why `frontId` cannot be chosen for this bay right now, as a sentence for
 * the user — or null when it can.
 *
 * Distinct from the profile membership above, and deliberately so: that says
 * which fronts EXIST for a run, this says which of them make sense for the
 * one bay in hand. A front withheld here is still the profile's; it is the
 * bay that rules it out.
 *
 * The one rule so far: a HOSTED bay cannot become a tall unit. `bays.js`
 * defines those as the kinds whose span the run fills with a base cabinet
 * because something drops into the counter above it — a sink, a cooktop —
 * and a tall unit rises through exactly that counter. The engine does not
 * refuse it today: forcing a `tall` override onto a sink bay replaces the
 * sink base with a "Tall Cabinet" and leaves the basin and the countertop
 * where they were, which is not a layout anyone asked for. So the picker
 * closes the door rather than reporting the mess afterwards.
 *
 * And on a vendor line every box front is a part of its own (item #23,
 * widened 2026-10-21): Venus sells a drawer base only at some widths (no 9",
 * 39" or 42"), a two-drawer base only at 24" and 30", a sink base from 24"
 * with no 27", and no two-drawer vanity base at all. Asked of the box as it
 * would be with that front, read the way the estimate prices it — the one
 * rule in `bayWidths.js` that the width drag and the width chips ask too, so
 * the picker cannot offer a part the estimate would then price as missing.
 * A Tall is asked at each height the line makes a pantry, since the fill
 * stands it at one of those. Held over every corpus bay by
 * `runFillInvariants.nightly.test.js`. Needs the bay's `run` and its built `cabinet`; without them it says nothing
 * about the part.
 *
 * And under a SINK on such a line, only the line's sink cabinets
 * (`sinkUnits.js`, the user 2026-10-30): Venus makes none that is drawers
 * only or plain doors, so a sink bay there takes the Sink front alone, and
 * its Sink cabinet row picks among the line's sink cabinets at that width.
 *
 * Returns a REASON rather than a boolean because a greyed control that
 * cannot say why is a worse control than one that is simply absent.
 */
function frontUnavailableReason(
  frontId,
  { bayKind, run, cabinet, objects } = {}
) {
  if (frontId === 'tall' && HOSTED_BAY_KINDS.has(bayKind)) {
    return bayKind === 'sink-base'
      ? 'A sink sits in the counter over this cabinet'
      : 'A cooktop sits in the counter over this cabinet';
  }
  const underSink = sinkBayFrontReason(run, bayKind, frontId);
  if (underSink) return underSink;
  // floating shelves come in stock lengths and are never cut
  if (frontId === 'floating-shelves' && run && cabinet)
    return floatingShelfReason(run, cabinet);
  // an end shelf is 12" wide, a size the line makes, at an end of its run
  if (frontId === 'end-shelf' && run && cabinet)
    return endShelfReason(run, cabinet, objects);
  // an angled end is its tier's size, at a run's open end
  if (frontId === 'angled-end' && run && cabinet)
    return angledEndReason(run, cabinet, objects);
  // a pull-out base is sold at one width
  if (PULL_OUTS[frontId] && run && cabinet)
    return pullOutReason(run, cabinet, frontId);
  // a microwave cabinet is 30" wide, a wall one 30" / 36" / 42" tall
  if (MICROWAVE_CABINETS[frontId] && run && cabinet)
    return microwaveCabinetReason(run, cabinet, frontId);
  // a wall cabinet with drawers is 18" wide, 48" / 54" / 60" over the counter
  if (frontId === 'wall-drawers' && run && cabinet)
    return wallDrawersReason(run, cabinet, objects);
  // a valance hangs between two uppers, at any width a valance comes in
  if (frontId === 'valance') return valanceBayReason(run, cabinet, objects);
  // a rack comes in its own widths, and needs the height it stands
  if (RACKS[frontId] && run && cabinet)
    return rackReason(run, cabinet, frontId);
  if (run && cabinet) {
    return frontWidthUnavailableReason(run, cabinet, frontId);
  }
  return null;
}

/**
 * The width a front is sold at, metres — for the fronts a line makes at one
 * width (the pull-outs, the microwave cabinets, the wall cabinet with
 * drawers, the end shelf), and floating shelves' stock length nearest the
 * bay. Picking one sets the bay to it (the user, 2026-11-01: "one click
 * width change (w/ a warning)"), so the bay keeps that width when the run
 * re-fits round it. Null for any other front.
 */
function fixedFrontWidthM(frontId, run, cabinet) {
  const IN = 0.0254;
  let widthIn =
    PULL_OUTS[frontId]?.widthIn ?? MICROWAVE_CABINETS[frontId]?.widthIn ?? null;
  if (frontId === 'wall-drawers') widthIn = WALL_DRAWERS.widthIn;
  if (frontId === 'end-shelf') widthIn = END_SHELF_WIDTH_IN;
  // a rack, the width it comes in nearest the bay's
  if (RACKS[frontId]) widthIn = rackWidthM(frontId, cabinet) / IN;
  // an angled end, its tier's
  if (frontId === 'angled-end')
    widthIn =
      ANGLED_ENDS[
        cabinet?.type === 'upper-cabinet' ||
        cabinet?.category?.['upper-cabinet']
          ? 'wall'
          : 'base'
      ].widthIn;
  if (frontId === 'floating-shelves') {
    const now = (cabinet?.dimensions?.x ?? 0) / IN;
    const lengths = floatingShelfUnits(familyOf(run)).map((u) => u.w);
    widthIn = lengths.length
      ? lengths.reduce((a, b) =>
          Math.abs(b - now) < Math.abs(a - now) ? b : a
        )
      : null;
  }
  return widthIn != null ? widthIn * IN : null;
}

/**
 * The width one click would set this bay to so it can wear `frontId` — when
 * the front is closed here ONLY by its width. Null when it is open as the bay
 * stands, or closed for another reason too (an upper's height, a run's end):
 * then the picker keeps its reason.
 */
function widthFixFor(frontId, ctx = {}) {
  const { run, cabinet } = ctx;
  // An end shelf belongs at the run's END: re-sized in place, the bay would
  // leave the rest of its old width at the end, and stand there no longer.
  if (frontId === 'end-shelf' || frontId === 'angled-end') return null;
  const widthM = fixedFrontWidthM(frontId, run, cabinet);
  if (!widthM || !cabinet?.dimensions) return null;
  if (!frontUnavailableReason(frontId, ctx)) return null;
  const resized = {
    ...cabinet,
    dimensions: { ...cabinet.dimensions, x: widthM },
  };
  return frontUnavailableReason(frontId, { ...ctx, cabinet: resized })
    ? null
    : widthM;
}

/**
 * The one click that moves the run's uppers so this bay can wear `frontId` —
 * the wall cabinet with drawers, which stands on the counter up to their top
 * (`wallDrawers.wallDrawersHangFix`) — or null, for any other front.
 * `upperBand` is the run's band with its `standardHang`.
 */
function hangFixFor(frontId, { run, cabinet, objects, upperBand } = {}) {
  return frontId === 'wall-drawers'
    ? wallDrawersHangFix(run, cabinet, objects, upperBand)
    : null;
}

/** An entry with no `profiles` belongs to every profile. */
function offeredIn(entry, profileId) {
  return !entry.profiles || entry.profiles.includes(profileId);
}

/**
 * Resolve to a profile id the catalog can answer for. UNSET and UNKNOWN both
 * become the kitchen, exactly as `profileOf` resolves them — resolving only
 * the unset case would leave an unrecognised id matching nothing but the
 * unannotated entries, so a typo or a profile from a newer build would
 * silently strip a run of its fridge bay rather than fall back.
 */
function resolveProfileId(profileId) {
  return profileById(profileId)?.id || KITCHEN_PROFILE_ID;
}

/**
 * The fronts this profile offers, in picker order. An unknown or missing
 * profile resolves to the kitchen's set, matching `profileOf` — a run stored
 * before profiles existed keeps every option it has always had.
 */
function frontTypesFor(profileId) {
  const id = resolveProfileId(profileId);
  return FRONT_TYPES.filter((f) => offeredIn(f, id));
}

/** The UPPER fronts this profile offers, in picker order. */
function upperFrontTypesFor(profileId) {
  const id = resolveProfileId(profileId);
  return UPPER_FRONT_TYPES.filter((f) => offeredIn(f, id));
}

/** The appliance bays this profile offers. Same unknown-profile rule. */
function applianceBaysFor(profileId) {
  const id = resolveProfileId(profileId);
  return APPLIANCE_BAYS.filter((a) => offeredIn(a, id));
}

/** Add Elements tray ids for this profile's appliance bays. */
function applianceElementsFor(profileId) {
  return applianceBaysFor(profileId).map((a) => a.element);
}

/**
 * The ONE id → label lookup: the picker's name, or its `short` header form.
 * Covers the read-only fronts too, which are labelled but never offered.
 * Deliberately NOT profile-scoped — a bay already wearing a front must be
 * nameable whatever profile is asking, or a profile change would blank the
 * header instead of showing what is actually there.
 */
function frontLabelOf(type, { short = false } = {}) {
  const option =
    FRONT_TYPES.find((o) => o.id === type) ||
    UPPER_FRONT_TYPES.find((o) => o.id === type) ||
    READ_ONLY_FRONTS.find((o) => o.id === type);
  if (!option) return undefined;
  return short ? option.short : option.name;
}

module.exports = {
  FRONT_GROUPS,
  FRONT_TYPES,
  UPPER_FRONT_TYPES,
  READ_ONLY_FRONTS,
  APPLIANCE_BAYS,
  KITCHEN_PROFILE_ID,
  VANITY_PROFILE_ID,
  frontTypesFor,
  upperFrontTypesFor,
  frontUnavailableReason,
  fixedFrontWidthM,
  widthFixFor,
  hangFixFor,
  applianceBaysFor,
  applianceElementsFor,
  frontLabelOf,
};
